import { useEffect, useState, ReactNode } from "react";

import { show } from "@intercom/messenger-js-sdk";
import { styled } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { Link, useNavigate } from "react-router-dom";

import { SubmitButton } from "@/common/components/buttons/submit-button/SubmitButton";
import { clearAllUserData } from "@/common/services/auth/Auth.service";

const REDIRECT_SECONDS = 10;

type Props = {
  buttonCopy: ReactNode;
  description: ReactNode;
  heading: ReactNode;
  type?: "error" | "success";
};

const EAgriImportNotification = ({
  buttonCopy,
  description,
  heading,
  type = "success",
}: Props) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    clearAllUserData();
  }, []);

  useEffect(() => {
    if (type !== "success") return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/login");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [type, navigate]);

  return (
    <Wrapper>
      <Heading>{heading}</Heading>
      <Description>{description}</Description>
      {type === "error" && (
        <SubmitButton onClick={() => show()} pending={false}>
          {buttonCopy}
        </SubmitButton>
      )}
      <LoginLink to="/login">
        <FormattedMessage id="reset.linkToLogin" />
        {type === "success" && (
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
          <span> ({countdown}s)</span>
        )}
      </LoginLink>
    </Wrapper>
  );
};

const Wrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  textAlign: "center",
});

const Heading = styled("h5")({
  fontWeight: 500,
  fontSize: 24,
  marginTop: 16,
  marginBottom: 0,
});

const Description = styled("p")({
  fontSize: 16,
  margin: 0,
});

const LoginLink = styled(Link)(({ theme }) => ({
  color: theme.palette.cf.cfPrimary,
  fontSize: 14,
}));

export { EAgriImportNotification };
